import React, { useCallback, useState, useEffect, useRef } from "react"
import Form from "@bappweb/form"
import Panel from "@bappweb/panel"
import TextField from "@bappweb/textfield"
import Button from "@bappweb/button"
import Checkbox from "@bappweb/checkbox"
import Link from "@bappweb/link"
import Captcha from "@smpu-web/nested/form/captcha"
import FormMessage from "@smpu-web/globalmodules/form-message/index"
import Page from "@smpu-web/globalmodules/page"
import Element from "@bappweb/element"
import RadioGroup from "@bappweb/radiogroup"
import Radio from "@bappweb/radio"
import { numeric, RegexpTypes, required, minLength } from "@bappweb/utils"
import { useGetTokenByTckn1Mutation, useGetCaptchaMutation, useRefreshCaptchaMutation, useApplyMutation } from "../../api/services"
import "./assets/style.scss"
import "src/assets/styles/style.scss"
import "../../assets/style.scss"


type PropsType = {
  onError?: (event?: React.BaseSyntheticEvent, errorField?: Record<string, any>) => void
  onSubmit?: (event?: React.BaseSyntheticEvent, values?: Record<string, any>) => void
  onItemsFetched?: any,
  reportYear: number
}

const DEBOUNCE_IN_MS = 500

function MainForm({ onError, onSubmit, onItemsFetched, reportYear }: PropsType) {

  const refForm = useRef(null)
 
  const [name, setName] = useState('')
  const [surname, setSurname] = useState('')
  const [accountOwnerType, setAccountOwnerType] = useState('K')
  const [unvan, setUnvan] = useState('')
  
  const [debouncedName, setDebouncedName] = useState('')
  const [debouncedSurname, setDebouncedSurname] = useState('')
  const [debouncedUnvan, setDebouncedUnvan] = useState('')

  const [captchaValues, setCaptchaValues] = useState({
    tokenSessionId: "",
    captchaData: "",
    captcha: "",
    showCaptcha: false,
    captchaValidate: true
  })

  const [error, setError] = useState<string>()
  const [isFetching, setIsFetching] = useState(false)
    
  const [getCaptcha] = useGetCaptchaMutation();
  const [getTokenByTckn1] = useGetTokenByTckn1Mutation();

  const makeCaptchaRequest = (guid:string) => {
    setIsFetching(true)
    getCaptcha({
      guid: guid,
      TokenSessionId: captchaValues.tokenSessionId,
      Language: 'tr-TR'
    })
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setError('')
          setCaptchaValues({
            ...captchaValues,
            showCaptcha: true,
            captchaData: payload.captchaData,
            captchaValidate: true
          })
        }
        else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => { setIsFetching(false) })
  }

  const makeTokenRequest = (guid: string) => {
    onItemsFetched([])
    getTokenByTckn1(guid)
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setError('')
          setCaptchaValues({
            ...captchaValues,
            tokenSessionId: payload.tokenSessionId,
          })
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => { 
        setIsFetching(false) 
      })
  }

  const clearCaptcha = () => {
    setCaptchaValues({
      ...captchaValues,
      tokenSessionId: "",
      captchaData: "",
      showCaptcha: false
    })

    // @ts-ignore TODO
    refForm.current?.setValue("captcha", null)
  }

  useEffect(() => {
    const fullName = name + '-' + surname

    if (captchaValues.captchaData === "" && captchaValues.tokenSessionId !== "") {
      makeCaptchaRequest(accountOwnerType === "K" ? fullName : unvan)
    }
  }, [captchaValues, getCaptcha, accountOwnerType, name, surname, unvan])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedName(name);
    }, DEBOUNCE_IN_MS);
    return () => clearTimeout(timeoutId);
  }, [name]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSurname(surname);
    }, DEBOUNCE_IN_MS);
    return () => clearTimeout(timeoutId);
  }, [surname]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedUnvan(unvan);
    }, DEBOUNCE_IN_MS);
    return () => clearTimeout(timeoutId);
  }, [unvan]);


  useEffect(() => {
    const fullName = debouncedName + '-' +  debouncedSurname

    if (debouncedName.length >= 2 && debouncedSurname.length >= 2 && fullName.length >= 5) {
      setIsFetching(true)
      makeTokenRequest(fullName)
    }
  }, [debouncedName, debouncedSurname]);


  useEffect(() => {
    if (debouncedUnvan.length >= 3) {
      setIsFetching(true)
      makeTokenRequest(debouncedUnvan)
    }
  }, [debouncedUnvan]);


  const handleChange = useCallback((e) => {
    const formName = e.target.name
    const formValue = e.target.value

    let fullName = ''

    switch (formName) {
      case "name":
        setName(formValue)
        clearCaptcha()
        break;
      case "surname":
        setSurname(formValue)
        clearCaptcha()
        break;
      case "accountOwnerType":
        setAccountOwnerType(formValue)
        if (formValue === "K"){
          setUnvan('')
          // @ts-ignore TODO
          refForm.current?.setValue("unvan", null)
        } else if (formValue === "T"){
          setName('')
          setSurname('')
          // @ts-ignore TODO
          refForm.current?.setValue("name", null)
          // @ts-ignore TODO
          refForm.current?.setValue("surname", null)
        }
        clearCaptcha()
        onItemsFetched([])
        break;
      case "unvan":
          setUnvan(formValue);
          clearCaptcha()
          break;
      case "captcha":
        setCaptchaValues({
          ...captchaValues,
          captcha: formValue,
          captchaValidate: true
        })
        break;
      default:
        break;
    }
  }, [getTokenByTckn1, captchaValues])

  const [refreshCaptcha] = useRefreshCaptchaMutation();
  const handleCaptchaRefresh = useCallback(() => {
    const fullName = (accountOwnerType === "K" ? (name + '-' + surname) : unvan)
    setIsFetching(true)
    refreshCaptcha({
      guid: fullName,
      TokenSessionId: captchaValues.tokenSessionId,
      Language: 'tr-TR'
    })
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setError('')
          setCaptchaValues({
            ...captchaValues,
            captchaData: payload.captchaData,
            captchaValidate: true
          })
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => { setIsFetching(false) })
  }, [refreshCaptcha, captchaValues, name, surname])

  const [apply] = useApplyMutation()

  const handleSubmit = () => {
    const guid = accountOwnerType === "K" ? name + '-' + surname : unvan
    const captchaVal = captchaValues && captchaValues.captcha ? captchaValues.captcha.toUpperCase() : ''
    setIsFetching(true)
    apply({
      guid,
      accountOwnerType,
      name,
      surname,
      unvan,
      reportYear,
      captcha: captchaVal,
      TokenSessionId: captchaValues.tokenSessionId
    })
      .unwrap()
      .then((payload) => {       
        console.log(payload) 
        if (!payload.IsError) {
          console.log(payload)
          setError('')
          if (onItemsFetched){
            onItemsFetched(payload.items)
            window.scrollTo({
              top: 1000,
              behavior: "smooth",
          });
          }
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")          
        }
      })
      .finally(() => { 
        const fullName = (accountOwnerType === "K" ? (name + '-' + surname) : unvan)
          refreshCaptcha({
            guid: fullName,
            TokenSessionId: captchaValues.tokenSessionId,
            Language: 'tr-TR'
          })
            .unwrap()
            .then((payload) => {
              if (!payload.IsError) {
                setCaptchaValues({
                  ...captchaValues,
                  captcha : "",
                  showCaptcha: true,
                  captchaData: payload.captchaData,
                  captchaValidate: false
                })
              }
              else {
                setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              }
            })
            .finally(() => { setIsFetching(false) })
      })
  }

  return (
    <Page>
      <Form onSubmit={handleSubmit} onChange={handleChange} grid={false} ref={refForm}>
        <Panel hasFooter connected={false} loading={isFetching}>
          <FormMessage className={"form-msg-error"} error={error}></FormMessage>
          <Panel.Body className="bapp-panel-body-transparent">
            <RadioGroup
              value={accountOwnerType}
              name="accountOwnerType"
              block={false} 
              className="ytm-hesapsahibi-radio">
              <Radio label="Kişisel" name="accountOwnerType" value="K" />
              <Radio label="Tüzel" name="accountOwnerType" value="T" />
            </RadioGroup>
            {accountOwnerType === "K" &&
            <>
              <TextField
                label='Ad'
                type="text"
                name="name"
                value={name}
                validations={[required("Ad alanı zorunludur."), 
                              minLength(2,"En az iki karakter girilmelidir.")
                            ]}
                validator={RegexpTypes.AlphaTr}            
              />
              <TextField
                label='Soyad'
                type="text"
                name="surname"
                value={surname}
                validations={[required("Soyad alanı zorunludur."),
                              minLength(2,"En az iki karakter girilmelidir.")
                            ]}
                validator={RegexpTypes.AlphaTr}
              />
            </>
            }
            {accountOwnerType === "T" &&
            <>
              <TextField
                label='Ünvan'
                type="text"
                name="unvan"
                value={unvan}
                validations={[required("Ünvan alanı zorunludur."),
                              minLength(3,"En az üç karakter girilmelidir.")
                            ]}
                validator={"^[a-zA-ZşŞıİçÇöÖüÜĞğ \\.\\s]+$"}
              />
            </>
            }            
            {captchaValues.showCaptcha && (
              <Captcha
                label="Güvenlik Kodu"
                id="captcha"
                type="text"
                name="captcha"
                placeholder="Güvenlik Kodu"
                captcha={captchaValues.captchaData}
                value={captchaValues.captcha}
                maxLength={4}
                maxRefreshCount={3}
                captchaValidate = {captchaValues.captchaValidate}
                refreshClick={handleCaptchaRefresh}
                validations={[required("Lütfen güvenlik kodunu giriniz.")]}
              />
            )}

          </Panel.Body>
          <Panel.Footer>
            <Panel.RightFooter>
              <Button
                text="Ara" type="submit" fetching={isFetching} disabled={isFetching}
                onClick={() =>
                  setCaptchaValues({
                    ...captchaValues,
                    captchaValidate: true
                  })
                } />
            </Panel.RightFooter>
          </Panel.Footer>
        </Panel>
      </Form>
    </Page>
  )
}

export default MainForm
